/* assets/cbi-interiors.js
 * Interiors index: the list view as a one screen stage, and the switch to the
 * grid view. Interiors project: the hero fade.
 *
 * List view (the stage): the page is held at the top, one screen tall, with
 * no footer. A scroll down (wheel, trackpad, swipe, arrow or page keys) moves
 * to the next project, a scroll up to the previous one, wrapping at the ends:
 * the current text and image fade out upwards, the next fade in from below,
 * and cbi-scheme.js fades the page colours to the new slide, the resting
 * header text with them. Input is ignored during a move and for a short rest
 * after it, and one continuous wheel or trackpad gesture moves at most one
 * project, so momentum cannot skip several. Without JavaScript the slides
 * simply stack and scroll.
 *
 * Grid view: the page scrolls normally.
 *
 * A phone has both views and the same stage. What it has no room for is two
 * staggered columns, so its second view is the same slides stacked and
 * scrolling, on white, and the toggle names it List rather than Grid. The
 * white comes from a class on the root element, because the colour it is
 * covering is an inline style cbi-scheme.js writes on the body.
 *
 * The view comes from ?view=grid, else the visitor's last choice this visit
 * (sessionStorage), else the list. Switching view crossfades: the view on
 * screen fades out, the swap happens while neither is showing, then the
 * arriving one rises in, the grid's cards a beat apart. Reduced motion, and
 * the view chosen on load, swap outright.
 *
 * Project hero: the intro pins while its fade track scrolls past, and the
 * share of the track scrolled so far is written to --cbi-project-fade, which
 * the hero's opacity follows (cbi-interiors.css). One rect read per frame.
 */

(() => {
  const KEY = 'cbi-interiors-view';
  const STAGE_CLASS = 'cbi-interiors-stage';
  const PLAIN_CLASS = 'cbi-interiors-plain'; /* the phone's white second view */
  const WHEEL_MIN = 12; /* ignore the tiniest trackpad nudges */
  const GESTURE_GAP = 200; /* ms of wheel silence that ends a gesture */
  const SWIPE_MIN = 40;
  const CARD_CAP = 4; /* grid cards past this share the last card's delay, see cbi-interiors.css */
  const root = document.documentElement;

  const read = () => {
    try {
      return window.sessionStorage.getItem(KEY);
    } catch (error) {
      return null;
    }
  };

  const write = (view) => {
    try {
      window.sessionStorage.setItem(KEY, view);
    } catch (error) {
      /* Storage can be unavailable; the view still switches. */
    }
  };

  const ms = (el, name, fallback) => {
    const value = parseFloat(getComputedStyle(el).getPropertyValue(name));
    return Number.isNaN(value) ? fallback : value;
  };

  const recolour = () => window.dispatchEvent(new Event('resize'));

  const setup = (section) => {
    if (section.dataset.cbiInteriorsReady) return;
    section.dataset.cbiInteriorsReady = 'true';

    const toggle = section.querySelector('[data-cbi-interiors-toggle]');
    const text = section.querySelector('[data-cbi-interiors-toggle-text]');
    const views = section.querySelectorAll('[data-cbi-interiors-view]');
    const list = section.querySelector('[data-cbi-interiors-view="list"]');
    const slides = list ? Array.from(list.querySelectorAll('.cbi-interiors-slide')) : [];
    if (!toggle || !views.length) return;

    let current = 0;
    let locked = false;
    let gestureUsed = false;
    let lastWheel = 0;
    let touchY = null;
    let staged = false;

    /* Only the showing slide counts for the page colours, and only it can
     * take focus. */
    const mark = () => {
      slides.forEach((slide, i) => {
        const active = i === current;
        slide.classList.toggle('is-active', active);
        slide.inert = !active;
        if (active) {
          delete slide.dataset.cbiSchemeOff;
        } else {
          slide.dataset.cbiSchemeOff = '';
        }
      });
    };

    const go = (step) => {
      if (locked || slides.length < 2) return;
      locked = true;
      gestureUsed = true;

      const from = slides[current];
      current = (current + step + slides.length) % slides.length;
      const to = slides[current];
      list.style.setProperty('--cbi-interiors-dir', String(step));

      /* Park the incoming slide below (or above) without a transition, then
       * let both slides move. */
      to.classList.add('is-entering');
      void to.offsetWidth;
      from.classList.add('is-leaving');
      section.classList.add('is-moved');
      mark();
      to.classList.remove('is-entering');
      recolour();

      const move = ms(list, '--cbi-interiors-move', 1000);
      const rest = ms(list, '--cbi-interiors-rest', 300);
      window.setTimeout(() => from.classList.remove('is-leaving'), move);
      window.setTimeout(() => {
        locked = false;
      }, move + rest);
    };

    const onWheel = (event) => {
      if (!staged) return;
      event.preventDefault();
      const now = event.timeStamp;
      if (now - lastWheel > GESTURE_GAP) gestureUsed = false;
      lastWheel = now;
      if (gestureUsed || Math.abs(event.deltaY) < WHEEL_MIN) return;
      go(event.deltaY > 0 ? 1 : -1);
    };

    const onKey = (event) => {
      if (!staged || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      const target = event.target;
      if (target.closest && target.closest('input, textarea, select, [contenteditable]')) return;
      const forward = ['ArrowDown', 'PageDown', ' '].includes(event.key);
      const back = ['ArrowUp', 'PageUp'].includes(event.key);
      if (!forward && !back) return;
      if (event.key === ' ' && target.closest && target.closest('button, a')) return;
      event.preventDefault();
      go(forward ? 1 : -1);
    };

    const onTouchStart = (event) => {
      if (staged) touchY = event.touches[0].clientY;
    };

    const onTouchMove = (event) => {
      if (staged) event.preventDefault();
    };

    const onTouchEnd = (event) => {
      if (!staged || touchY === null) return;
      const dy = touchY - event.changedTouches[0].clientY;
      touchY = null;
      if (Math.abs(dy) < SWIPE_MIN) return;
      gestureUsed = false;
      go(dy > 0 ? 1 : -1);
    };

    const stage = (on) => {
      staged = on && slides.length > 0;
      section.classList.toggle('is-stage', staged);
      root.classList.toggle(STAGE_CLASS, staged);
      if (staged) {
        window.scrollTo({ top: 0, behavior: 'instant' });
        if (window.cbiLenis) window.cbiLenis.stop();
        mark();
      } else {
        if (window.cbiLenis) window.cbiLenis.start();
        section.classList.remove('is-moved');
        slides.forEach((slide) => {
          slide.classList.remove('is-active', 'is-leaving', 'is-entering');
          slide.inert = false;
          delete slide.dataset.cbiSchemeOff;
        });
      }
    };

    const phone = window.matchMedia('(max-width: 749px)');

    /* The button names the view it would switch to. On a phone that second
     * view is the stacked white one, which has a name of its own. */
    const badge = (view) => {
      const grid = view === 'grid';
      toggle.setAttribute('aria-pressed', String(grid));
      if (!text) return;
      const second = phone.matches
        ? toggle.dataset.gridLabelPhone || toggle.dataset.gridLabel
        : toggle.dataset.gridLabel;
      text.textContent = grid ? toggle.dataset.listLabel : second;
    };

    let chosen = 'list';

    const show = (view, { jump } = { jump: false }) => {
      chosen = view;
      /* The second view is the grid on a desktop and the stacked slides on a
       * phone, which are the same markup as the first view, held plain. */
      const plain = phone.matches && view === 'grid';
      const shown = plain ? 'list' : view;
      for (const el of views) el.hidden = el.dataset.cbiInteriorsView !== shown;
      section.classList.toggle('is-plain', plain);
      root.classList.toggle(PLAIN_CLASS, plain);
      badge(view);
      stage(view !== 'grid' && !(window.Shopify && window.Shopify.designMode));
      if (jump) window.scrollTo({ top: 0, behavior: 'instant' });
      recolour();
    };

    /* Crossing the breakpoint re-applies the same choice: the same two views
     * either side of it, drawn the way that width draws them. */
    if (phone.addEventListener) {
      phone.addEventListener('change', () => {
        settle();
        show(chosen);
      });
    }

    /* The animated switch: fade the view on screen out, swap while nothing is
     * showing, then let the arriving one rise in (cbi-interiors.css). Clicking
     * again mid switch clears the timers and starts over, so the classes
     * cannot be left behind. */
    let timers = [];

    const settle = () => {
      timers.forEach(window.clearTimeout);
      timers = [];
      section.classList.remove('is-switching', 'is-view-out', 'is-view-parked');
    };

    const switchTo = (view) => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        settle();
        show(view, { jump: true });
        return;
      }
      settle();
      section.classList.add('is-switching', 'is-view-out');
      const out = ms(section, '--cbi-interiors-view-out', 280);
      const arrive = ms(section, '--cbi-interiors-view-in', 480);
      const step = ms(section, '--cbi-interiors-view-step', 70);
      timers.push(
        window.setTimeout(() => {
          section.classList.remove('is-view-out');
          section.classList.add('is-view-parked');
          show(view, { jump: true });
          void section.offsetWidth;
          section.classList.remove('is-view-parked');
          timers.push(
            window.setTimeout(() => section.classList.remove('is-switching'), arrive + step * CARD_CAP)
          );
        }, out)
      );
    };

    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('keydown', onKey);
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd, { passive: true });

    const param = new URLSearchParams(window.location.search).get('view');
    const initial = param === 'grid' || param === 'list' ? param : read();
    show(initial === 'grid' ? 'grid' : 'list');

    toggle.addEventListener('click', () => {
      const next = toggle.getAttribute('aria-pressed') === 'true' ? 'list' : 'grid';
      write(next);
      badge(next);
      switchTo(next);
    });
  };

  const fade = (intro) => {
    if (intro.dataset.cbiFadeReady) return;
    intro.dataset.cbiFadeReady = 'true';
    const track = intro.querySelector('.cbi-project__fade-track');
    if (!track) return;
    intro.classList.add('is-fading');

    let ticking = false;
    const apply = () => {
      ticking = false;
      const distance = track.offsetHeight || 1;
      const share = Math.min(1, Math.max(0, -intro.getBoundingClientRect().top / distance));
      intro.style.setProperty('--cbi-project-fade', share.toFixed(4));
      intro.classList.toggle('is-faded', share >= 1);
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    apply();
  };

  const start = () => {
    document.querySelectorAll('[data-cbi-interiors]').forEach(setup);
    document.querySelectorAll('[data-cbi-project-intro]').forEach(fade);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
  document.addEventListener('shopify:section:load', start);
})();
